// B2 — Research pane (center). The evidence hierarchy, UX rule #2:
// ALWAYS in this order so a non-expert never has to read a raw diff
// to decide —
//   1. the decision question + clickable records (open the real
//      record in the drawer — UX fix: records are now inspectable)
//   2. probable-case verdict (plain language — why it was flagged)
//   3. structured flag reasons
//   4. grouped evidence (collapsed by default)
//   5. REAL side-by-side field comparison for a 2-record pair, with
//      match / conflict / only-one-side coloring (what stays vs goes)
//
// The center is now information-rich (matching the Claude design):
// the steward sees the actual records, the actual field values, and
// what differs — before they ever click an action.

import { type CSSProperties, useEffect, useMemo, useState } from "react";

import {
  api,
  ApiError,
  type CleanupIssueDetailResponse,
  type ContactDetailHouseholdSummary,
  type SafeContact,
} from "../api";
import { Button } from "../ui";
import { MERGEABLE_FIELDS_UI } from "../components/merge-review-helpers";
import {
  comparablePair,
  displayLabel,
  kindLabel,
  tierChip,
  type RecordRef,
} from "./cleanup-records";
import { recordDeepLink } from "./record-links";
import { commissionFlowPath, isCommissionIssue } from "./commission-issue";
import { formatRelativeTime } from "../utils/formatting";
import { ExternalLink } from "lucide-react";
import {
  fmtBirthday,
  memberName,
  roleLabel,
  sameHouseholdHint,
} from "./household-roster-helpers";

function asLines(v: unknown): string[] {
  if (v == null) return [];
  if (Array.isArray(v)) return v.map((x) => String(x)).filter(Boolean);
  if (typeof v === "string") return v ? [v] : [];
  return [String(v)];
}

const GROUP_LABELS: Record<string, string> = {
  identity: "Identity signals",
  relationship: "Relationship",
  sourceTrust: "Source trust",
  recency: "Recency",
  quality: "Data quality",
  // A cancellation notice's "did they actually leave, or just switch
  // carriers?" finding. An unlabelled key renders with its raw name as
  // the heading, so a new evidence group belongs here the day it ships.
  replacement: "Replacement policy",
};

function RecRefButton({
  r,
  onOpen,
}: {
  r: RecordRef;
  onOpen: (r: RecordRef) => void;
}) {
  return (
    <button
      className="cu-rec-ref"
      onClick={() => onOpen(r)}
      title={`Open ${r.label}`}
      style={{
        border: "none",
        background: "transparent",
        padding: 0,
        font: "inherit",
        color: "var(--blue)",
        fontWeight: 800,
        cursor: "pointer",
        textDecoration: "underline",
        textUnderlineOffset: 2,
      }}
    >
      {displayLabel(r.label)}
    </button>
  );
}

// ── side-by-side field comparison (real data) ────────────────────────

type Cmp = {
  label: string;
  a: string;
  b: string;
  kind: "match" | "conflict" | "one-side";
};

// Name-ish fields get display-name normalization so an AC-imported
// "PETA" / "SIMPSON" reads "Peta" / "Simpson" (Grant's all-caps rule).
const NAME_FIELDS = new Set<string>([
  "legal_first_name",
  "legal_middle_name",
  "legal_last_name",
  "preferred_name",
]);

function fieldValue(c: SafeContact, key: string): string {
  const raw = (c as unknown as Record<string, unknown>)[key];
  if (raw == null) return "";
  if (typeof raw === "boolean") return raw ? "yes" : "no";
  const s = String(raw).trim();
  if (s === "") return "";
  return NAME_FIELDS.has(key) ? displayLabel(s) : s;
}

// Every mergeable field (the same ~29 the merge wizard uses), so the
// steward sees ALL data on both records — Grant: "the more fields the
// better when trying to merge." Rows where BOTH sides are blank are
// dropped (no signal); everything populated on either side shows.
function buildComparison(a: SafeContact, b: SafeContact): Cmp[] {
  return MERGEABLE_FIELDS_UI.map((f): Cmp | null => {
    const an = fieldValue(a, f.key);
    const bn = fieldValue(b, f.key);
    if (an === "" && bn === "") return null;
    let kind: Cmp["kind"];
    if (an !== "" && bn !== "") {
      kind = an.toLowerCase() === bn.toLowerCase() ? "match" : "conflict";
    } else {
      kind = "one-side";
    }
    return { label: f.label, a: an, b: bn, kind };
  }).filter((x): x is Cmp => x !== null);
}

function CmpCell({ value, kind }: { value: string; kind: Cmp["kind"] }) {
  return (
    <div
      style={{
        fontSize: 13,
        color: value ? "var(--navy)" : "var(--fg-muted)",
        wordBreak: "break-word",
      }}
    >
      {value || "(empty)"}
      {kind === "conflict" && (
        <span
          style={{
            marginLeft: 6,
            fontSize: 10,
            fontWeight: 800,
            color: "var(--danger)",
          }}
        >
          ≠
        </span>
      )}
    </div>
  );
}

type CmpFilter = "all" | "diff" | "conflict";

// ── household & member roster (Baker: same person, or two people?) ───
//
// When /cleanup flags two contacts as a possible duplicate, Baker's
// first question is "is this really ONE person entered twice, or two
// DIFFERENT people who just share a phone / last name (spouses)?".
// In AC he read the "INSURANCE: Household Members 18+" notes. That
// field was never migrated; the OS already models households + members
// first-class, so we render each flagged contact's household and its
// co-members (name · role · DOB · age) right here — no trip to AC, no
// leaving /cleanup. This is decision CONTEXT and sits ABOVE the raw
// field diff (UX rule #2: never make a non-expert read a diff first).

function HouseholdSide({
  heading,
  headingColor,
  divider,
  selfContactId,
  hh,
  onOpenRecord,
}: {
  heading: string;
  headingColor: string;
  divider: boolean;
  selfContactId: string;
  hh: ContactDetailHouseholdSummary | null;
  onOpenRecord: (r: RecordRef) => void;
}) {
  const wrap: CSSProperties = {
    padding: "10px 12px",
    background: "var(--bg-0)",
    borderLeft: divider ? "1px solid var(--mid-gray)" : "none",
  };
  const eyebrow = (
    <div
      style={{
        fontSize: 9.5,
        fontWeight: 800,
        textTransform: "uppercase",
        letterSpacing: "0.06em",
        color: headingColor,
        marginBottom: 4,
      }}
    >
      {heading}
    </div>
  );
  if (!hh) {
    return (
      <div style={wrap}>
        {eyebrow}
        <div style={{ fontSize: 13, color: "var(--fg-muted)" }}>
          No household — this contact isn’t linked to a household.
        </div>
      </div>
    );
  }
  const self = hh.members.find((m) => m.id === selfContactId) ?? null;
  const others = hh.members.filter((m) => m.id !== selfContactId);
  return (
    <div style={wrap}>
      {eyebrow}
      <button
        className="cu-household-open"
        onClick={() =>
          onOpenRecord({
            kind: "household",
            id: hh.id,
            label: hh.display_name,
          })
        }
        title={`Open ${hh.display_name}`}
        style={{
          border: "none",
          background: "transparent",
          padding: 0,
          font: "inherit",
          color: "var(--blue)",
          fontWeight: 800,
          cursor: "pointer",
          textDecoration: "underline",
          textUnderlineOffset: 2,
          display: "block",
          marginBottom: 4,
        }}
      >
        {displayLabel(hh.display_name)}
      </button>
      <div style={{ fontSize: 12, color: "var(--fg-3)", marginBottom: 6 }}>
        This record:{" "}
        <b style={{ color: "var(--navy)" }}>
          {self ? roleLabel(self.household_role) : "in this household"}
        </b>{" "}
        · {fmtBirthday(self?.birthday ?? null)}
      </div>
      {others.length === 0 ? (
        <div style={{ fontSize: 12, color: "var(--fg-muted)" }}>
          Only member of this household.
        </div>
      ) : (
        <ul style={{ margin: 0, paddingLeft: 16 }}>
          {others.map((m) => (
            <li
              key={m.id}
              style={{
                fontSize: 12.5,
                color: "var(--fg-3)",
                marginBottom: 2,
              }}
            >
              <b style={{ color: "var(--navy)" }}>{memberName(m)}</b> —{" "}
              {roleLabel(m.household_role)} · {fmtBirthday(m.birthday)}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function HouseholdRosterPanel({
  contactAId,
  contactBId,
  hhA,
  hhB,
  birthdayA,
  birthdayB,
  onOpenRecord,
}: {
  contactAId: string;
  contactBId: string;
  hhA: ContactDetailHouseholdSummary | null;
  hhB: ContactDetailHouseholdSummary | null;
  birthdayA: string | null;
  birthdayB: string | null;
  onOpenRecord: (r: RecordRef) => void;
}) {
  const hint = sameHouseholdHint({
    householdAId: hhA?.id ?? null,
    householdALabel: hhA?.display_name ?? null,
    householdBId: hhB?.id ?? null,
    birthdayA,
    birthdayB,
  });
  return (
    <div style={{ marginBottom: 12 }}>
      <div
        style={{
          fontSize: 11,
          fontWeight: 800,
          textTransform: "uppercase",
          letterSpacing: "0.08em",
          color: "var(--blue-dark)",
          marginBottom: 6,
        }}
      >
        Household &amp; members — same person, or two people?
      </div>
      {hint && (
        <div
          style={{
            border:
              hint.tone === "warn"
                ? "1px solid var(--danger)"
                : "1px solid var(--blue)",
            background:
              hint.tone === "warn"
                ? "rgba(192,57,43,0.06)"
                : "var(--blue-tint)",
            color: "var(--navy)",
            borderRadius: 8,
            padding: "8px 10px",
            fontSize: 12.5,
            lineHeight: 1.45,
            marginBottom: 8,
          }}
        >
          {hint.text}
        </div>
      )}
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "1fr 1fr",
          border: "1px solid var(--mid-gray)",
          borderRadius: 8,
          overflow: "hidden",
        }}
      >
        <HouseholdSide
          heading="Survivor (kept)"
          headingColor="var(--success)"
          divider={false}
          selfContactId={contactAId}
          hh={hhA}
          onOpenRecord={onOpenRecord}
        />
        <HouseholdSide
          heading="Will be merged in"
          headingColor="var(--fg-muted)"
          divider
          selfContactId={contactBId}
          hh={hhB}
          onOpenRecord={onOpenRecord}
        />
      </div>
    </div>
  );
}

function Comparison({
  pair,
  onOpenRecord,
  onDismissStale,
}: {
  pair: { a: RecordRef; b: RecordRef };
  onOpenRecord: (r: RecordRef) => void;
  onDismissStale?: () => void;
}) {
  const [a, setA] = useState<SafeContact | null>(null);
  const [b, setB] = useState<SafeContact | null>(null);
  const [hhA, setHhA] = useState<ContactDetailHouseholdSummary | null>(
    null,
  );
  const [hhB, setHhB] = useState<ContactDetailHouseholdSummary | null>(
    null,
  );
  const [err, setErr] = useState<string | null>(null);
  // #11 — true when the load failed because a record is gone (404),
  // i.e. the issue is stale and can be dismissed inline.
  const [stale, setStale] = useState(false);
  const [filter, setFilter] = useState<CmpFilter>("all");

  useEffect(() => {
    let cancelled = false;
    setA(null);
    setB(null);
    setHhA(null);
    setHhB(null);
    setErr(null);
    setStale(false);
    void (async () => {
      try {
        // getContactDetail (not getContact) so a single call per side
        // returns the SafeContact AND its household + member roster —
        // no extra round trip. The roster powers the spouse-vs-dup
        // decision aid above the field diff (Baker's feedback).
        const [ra, rb] = await Promise.all([
          api.getContactDetail(pair.a.id),
          api.getContactDetail(pair.b.id),
        ]);
        if (cancelled) return;
        setA(ra.contact);
        setB(rb.contact);
        setHhA(ra.household);
        setHhB(rb.household);
      } catch (e) {
        if (!cancelled) {
          // A 404 here almost always means one record was already
          // merged away or archived (the issue is stale and hasn't
          // been swept yet). Say so plainly — never a raw "HTTP 404"
          // (UX rule #5: no dead end).
          if (e instanceof ApiError && e.status === 404) {
            setStale(true);
            setErr(
              "One of these records no longer exists — it was likely already merged or archived. This issue is stale; it will clear on the next scan, or you can dismiss it now.",
            );
          } else {
            setErr(
              e instanceof ApiError
                ? e.message
                : "Couldn't load both records to compare.",
            );
          }
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [pair.a.id, pair.b.id]);

  const rows = useMemo(
    () => (a && b ? buildComparison(a, b) : []),
    [a, b],
  );
  const shown = rows.filter((r) =>
    filter === "all"
      ? true
      : filter === "conflict"
        ? r.kind === "conflict"
        : r.kind !== "match",
  );
  const conflicts = rows.filter((r) => r.kind === "conflict").length;
  const matches = rows.filter((r) => r.kind === "match").length;

  if (pair.a.kind !== "contact") {
    return null; // side-by-side is contact-only for now
  }

  return (
    <div style={{ marginTop: 6 }}>
      <div
        style={{
          fontSize: 11,
          fontWeight: 800,
          textTransform: "uppercase",
          letterSpacing: "0.08em",
          color: "var(--blue-dark)",
          marginBottom: 8,
        }}
      >
        Side-by-side — what stays vs. what goes
      </div>

      {err && (
        <div style={{ color: "var(--danger)", fontSize: 13 }}>
          {err}
          {stale && onDismissStale && (
            <div style={{ marginTop: 8 }}>
              <Button variant="ghost" size="sm" onClick={onDismissStale}>
                Dismiss this stale issue
              </Button>
            </div>
          )}
        </div>
      )}
      {!err && (!a || !b) && (
        <div style={{ color: "var(--fg-muted)", fontSize: 13 }}>
          Loading both records…
        </div>
      )}

      {a && b && (
        <>
          <HouseholdRosterPanel
            contactAId={a.id}
            contactBId={b.id}
            hhA={hhA}
            hhB={hhB}
            birthdayA={a.birthday}
            birthdayB={b.birthday}
            onOpenRecord={onOpenRecord}
          />
          <div
            style={{
              display: "flex",
              gap: 6,
              marginBottom: 8,
              flexWrap: "wrap",
            }}
          >
            {(
              [
                ["all", "All fields"],
                ["diff", "Differences"],
                ["conflict", "Conflicts only"],
              ] as Array<[CmpFilter, string]>
            ).map(([f, lbl]) => (
              <button
                key={f}
                className="cu-cmp-filter-chip"
                onClick={() => setFilter(f)}
                style={{
                  border:
                    filter === f
                      ? "1px solid var(--blue)"
                      : "1px solid var(--mid-gray)",
                  background:
                    filter === f ? "var(--blue-tint)" : "var(--bg-0)",
                  color: "var(--navy)",
                  borderRadius: 9999,
                  padding: "3px 10px",
                  fontSize: 11.5,
                  fontWeight: 700,
                  cursor: "pointer",
                }}
              >
                {lbl}
              </button>
            ))}
          </div>

          {/* header row: the two records, clickable */}
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "120px 1fr 1fr",
              gap: 0,
              border: "1px solid var(--mid-gray)",
              borderRadius: 8,
              overflow: "hidden",
            }}
          >
            <div style={{ background: "var(--light-gray)" }} />
            <div
              style={{
                background: "var(--light-gray)",
                padding: "8px 10px",
                borderLeft: "1px solid var(--mid-gray)",
              }}
            >
              <div
                style={{
                  fontSize: 9.5,
                  fontWeight: 800,
                  textTransform: "uppercase",
                  color: "var(--success)",
                }}
              >
                Survivor (kept)
              </div>
              <RecRefButton r={pair.a} onOpen={onOpenRecord} />
            </div>
            <div
              style={{
                background: "var(--light-gray)",
                padding: "8px 10px",
                borderLeft: "1px solid var(--mid-gray)",
              }}
            >
              <div
                style={{
                  fontSize: 9.5,
                  fontWeight: 800,
                  textTransform: "uppercase",
                  color: "var(--fg-muted)",
                }}
              >
                Will be merged in
              </div>
              <RecRefButton r={pair.b} onOpen={onOpenRecord} />
            </div>

            {shown.map((r) => {
              const bg =
                r.kind === "conflict"
                  ? "rgba(192,57,43,0.06)"
                  : r.kind === "match"
                    ? "rgba(31,122,58,0.06)"
                    : "var(--bg-0)";
              return (
                <div key={r.label} style={{ display: "contents" }}>
                  <div
                    style={{
                      padding: "8px 10px",
                      borderTop: "1px solid var(--mid-gray)",
                      fontSize: 10.5,
                      fontWeight: 800,
                      textTransform: "uppercase",
                      letterSpacing: "0.05em",
                      color: "var(--fg-muted)",
                      background: bg,
                    }}
                  >
                    {r.label}
                  </div>
                  <div
                    style={{
                      padding: "8px 10px",
                      borderTop: "1px solid var(--mid-gray)",
                      borderLeft: "1px solid var(--mid-gray)",
                      background: bg,
                    }}
                  >
                    <CmpCell value={r.a} kind={r.kind} />
                  </div>
                  <div
                    style={{
                      padding: "8px 10px",
                      borderTop: "1px solid var(--mid-gray)",
                      borderLeft: "1px solid var(--mid-gray)",
                      background: bg,
                    }}
                  >
                    <CmpCell value={r.b} kind={r.kind} />
                  </div>
                </div>
              );
            })}
          </div>

          <div
            style={{
              fontSize: 12,
              color: "var(--fg-muted)",
              marginTop: 8,
            }}
          >
            <b style={{ color: "var(--navy)" }}>{conflicts}</b> conflict
            {conflicts === 1 ? "" : "s"} ·{" "}
            <b style={{ color: "var(--navy)" }}>{matches}</b> identical ·{" "}
            {rows.length} compared. You choose the winner for every field
            in the merge review.
          </div>
        </>
      )}
    </div>
  );
}

// ── pane ─────────────────────────────────────────────────────────────

export function ResearchPane({
  issueId,
  onOpenRecord,
  onDismissStale,
}: {
  issueId: string | null;
  onOpenRecord: (r: RecordRef) => void;
  // #11 — invoked from the inline "Dismiss this stale issue" button the
  // Comparison shows when a record 404s (already merged/archived).
  onDismissStale?: () => void;
}) {
  const [data, setData] = useState<CleanupIssueDetailResponse | null>(
    null,
  );
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!issueId) {
      setData(null);
      return;
    }
    let cancelled = false;
    setLoading(true);
    setError(null);
    void (async () => {
      try {
        const d = await api.getCleanupIssue(issueId);
        if (!cancelled) setData(d);
      } catch (err) {
        if (!cancelled) {
          setError(
            err instanceof ApiError
              ? err.message
              : "Couldn't load this issue.",
          );
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [issueId]);

  if (!issueId) {
    return (
      <div
        style={{
          flex: 1,
          padding: 40,
          color: "var(--fg-muted)",
          fontSize: 14,
          textAlign: "center",
        }}
      >
        Select an issue from the queue to research it.
      </div>
    );
  }
  if (loading) {
    return (
      <div style={{ flex: 1, padding: 24, color: "var(--fg-muted)" }}>
        Loading issue…
      </div>
    );
  }
  if (error || !data) {
    return (
      <div style={{ flex: 1, padding: 24, color: "var(--danger)" }}>
        {error ?? "Issue not found."}
      </div>
    );
  }

  const { issue, notes } = data;
  // A human "Flag a problem" issue (createManualIssue writes
  // detector_slug='manual'). For these the detector confidence/risk
  // scores are meaningless — a person deliberately flagged it — so we
  // show WHO flagged it and WHEN instead. The flagger is the author of
  // the seed note (notes are ordered oldest-first).
  const isManualFlag = issue.detectorSlug === "manual";
  const flaggerName = notes[0]?.authorName?.trim() || null;
  const flagReasons = asLines(issue.flagReasons);
  const evidenceEntries = Object.entries(issue.evidence ?? {}).filter(
    ([, v]) => asLines(v).length > 0,
  );
  const pair = comparablePair(issue);

  return (
    <div
      style={{
        flex: 1,
        overflowY: "auto",
        maxHeight: "70vh",
        padding: "16px 20px",
      }}
    >
      {/* 1. The decision question + clickable records */}
      <div
        style={{
          fontSize: 18,
          fontWeight: 800,
          color: "var(--navy)",
          marginBottom: 4,
        }}
      >
        {issue.decisionQuestion}
      </div>
      <div
        style={{
          display: "flex",
          gap: 8,
          flexWrap: "wrap",
          alignItems: "center",
          marginBottom: 10,
          fontSize: 13,
          color: "var(--fg-3)",
        }}
      >
        {/* Donna 2026-07-30 — the kind prefix. These names used to render
            bare and side by side, so "Greg Household · Greg" (a household
            and its own only member) read as two households, and stewards
            tried to merge one into the other. The record kind is the
            difference and it was only visible further down the pane, in
            "Records involved". */}
        {issue.affectedRecords.map((r, i) => (
          <span key={`${r.kind}:${r.id}`}>
            {i > 0 && (
              <span style={{ color: "var(--fg-muted)", marginRight: 8 }}>·</span>
            )}
            <span
              style={{
                color: "var(--fg-muted)",
                fontSize: 10.5,
                fontWeight: 800,
                textTransform: "uppercase",
                letterSpacing: "0.06em",
                marginRight: 5,
              }}
            >
              {kindLabel(r.kind)}
            </span>
            <RecRefButton r={r} onOpen={onOpenRecord} />
          </span>
        ))}
        <span style={{ color: "var(--fg-muted)", fontSize: 12 }}>
          (click a name to inspect the full record)
        </span>
      </div>
      <div
        style={{
          display: "flex",
          gap: 8,
          flexWrap: "wrap",
          marginBottom: 14,
          fontSize: 11,
          color: "var(--fg-muted)",
        }}
      >
        {isManualFlag ? (
          <span>
            Flagged by{" "}
            <b style={{ color: "var(--navy)" }}>
              {flaggerName ?? "a team member"}
            </b>{" "}
            · {formatRelativeTime(issue.firstDetectedAt)}
          </span>
        ) : (
          <>
            <span title="Confidence: how sure the detector is that this is a real issue, 0–100%." style={{ cursor: "help" }}>
              <b style={{ color: "var(--navy)" }}>{issue.confidence}%</b>{" "}
              confident
            </span>
            <span title="Risk: how much damage acting wrongly could do, from 0 (harmless) to 100 (high-stakes). Low risk means it's safe to act quickly." style={{ cursor: "help" }}>
              · risk {issue.risk}
            </span>
          </>
        )}
        <span className={tierChip(issue.tier).className}>
          {tierChip(issue.tier).label}
        </span>
        <span>· {issue.issueFamily}</span>
        <span>· {issue.state}</span>
      </div>

      {/* 2. Probable-case verdict — plain language, prominent */}
      <div
        style={{
          background: "rgba(254,215,102,0.16)",
          border: "1px solid var(--yellow-deep)",
          borderRadius: 10,
          padding: "12px 14px",
          marginBottom: 14,
        }}
      >
        <div
          style={{
            fontSize: 10.5,
            fontWeight: 800,
            textTransform: "uppercase",
            letterSpacing: "0.1em",
            color: "var(--warning)",
            marginBottom: 4,
          }}
        >
          Why this was flagged
        </div>
        <div
          style={{ fontSize: 14, color: "var(--navy)", lineHeight: 1.5 }}
        >
          {issue.flagExplanation}
        </div>
      </div>

      {/* 2b. The note thread — the flagger's own words (the "details"
          the explanation used to only point at), plus any collaborative
          handoff notes added since. Read-back was previously missing:
          notes could be added but were rendered nowhere. */}
      {notes.length > 0 && (
        <div style={{ marginBottom: 14 }}>
          <div
            style={{
              fontSize: 11,
              fontWeight: 800,
              textTransform: "uppercase",
              letterSpacing: "0.08em",
              color: "var(--blue-dark)",
              marginBottom: 6,
            }}
          >
            {isManualFlag ? "Flagger's note" : "Notes"}
            {notes.length > 1 && (
              <span style={{ color: "var(--fg-muted)", fontWeight: 600 }}>
                {" "}
                ({notes.length})
              </span>
            )}
          </div>
          <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            {notes.map((n) => (
              <div
                key={n.id}
                style={{
                  background: "var(--bg-2)",
                  border: "1px solid var(--mid-gray)",
                  borderRadius: 8,
                  padding: "9px 12px",
                }}
              >
                <div
                  style={{
                    fontSize: 11.5,
                    color: "var(--fg-3)",
                    marginBottom: 3,
                  }}
                >
                  <b style={{ color: "var(--navy)" }}>
                    {n.authorName?.trim() || "Team member"}
                  </b>{" "}
                  · {formatRelativeTime(n.createdAt)}
                </div>
                <div
                  style={{
                    fontSize: 13.5,
                    color: "var(--navy)",
                    lineHeight: 1.5,
                    whiteSpace: "pre-wrap",
                  }}
                >
                  {n.body}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* 3. Structured flag reasons */}
      {flagReasons.length > 0 && (
        <div style={{ marginBottom: 14 }}>
          <div
            style={{
              fontSize: 11,
              fontWeight: 800,
              textTransform: "uppercase",
              letterSpacing: "0.08em",
              color: "var(--blue-dark)",
              marginBottom: 6,
            }}
          >
            What we matched on
          </div>
          <ul style={{ margin: 0, paddingLeft: 18 }}>
            {flagReasons.map((r, i) => (
              <li
                key={i}
                style={{
                  fontSize: 13.5,
                  color: "var(--fg-3)",
                  marginBottom: 3,
                }}
              >
                {r}
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* 4. Grouped evidence — collapsed by default (not a data dump) */}
      {evidenceEntries.length > 0 && (
        <details style={{ marginBottom: 14 }}>
          <summary
            className="cu-evidence-summary"
            style={{
              cursor: "pointer",
              fontSize: 11,
              fontWeight: 800,
              textTransform: "uppercase",
              letterSpacing: "0.08em",
              color: "var(--blue-dark)",
            }}
          >
            Evidence ({evidenceEntries.length} group
            {evidenceEntries.length === 1 ? "" : "s"})
          </summary>
          <div style={{ marginTop: 8 }}>
            {evidenceEntries.map(([key, val]) => (
              <div key={key} style={{ marginBottom: 8 }}>
                <div
                  style={{
                    fontSize: 12,
                    fontWeight: 700,
                    color: "var(--navy)",
                    marginBottom: 2,
                  }}
                >
                  {GROUP_LABELS[key] ?? key}
                </div>
                <ul style={{ margin: 0, paddingLeft: 18 }}>
                  {asLines(val).map((line, i) => (
                    <li
                      key={i}
                      style={{ fontSize: 13, color: "var(--fg-3)" }}
                    >
                      {line}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </details>
      )}

      {/* 5. Real side-by-side comparison for a 2-record contact pair */}
      {pair ? (
        <Comparison
          pair={pair}
          onOpenRecord={onOpenRecord}
          {...(onDismissStale ? { onDismissStale } : {})}
        />
      ) : (
        <div>
          <div
            style={{
              fontSize: 11,
              fontWeight: 800,
              textTransform: "uppercase",
              letterSpacing: "0.08em",
              color: "var(--blue-dark)",
              marginBottom: 6,
            }}
          >
            Records involved ({issue.affectedRecords.length})
          </div>
          {issue.affectedRecords.map((r) => {
            // Two ways to reach a record from an issue (Nate 2026-07-08:
            // "I need to click through to the household and see all its
            // transactions"): the button opens the quick inspector drawer
            // for research WITHOUT leaving the queue, and the "Open page"
            // link jumps straight to the full record page (new tab) — no
            // more inspector-then-"open full record" two-hop.
            // Commission issues (Baker 2026-07-30): their subject is a
            // commission_reports row with no record page, so the "Open
            // page" slot points at the commission flow instead — the
            // place the review actually happens — rather than vanishing.
            const page =
              recordDeepLink(r.kind, r.id) ??
              (isCommissionIssue(issue) ? commissionFlowPath(issue) : null);
            return (
              <div
                key={`${r.kind}:${r.id}`}
                style={{
                  display: "flex",
                  alignItems: "stretch",
                  gap: 6,
                  marginBottom: 6,
                }}
              >
                <button
                  className="cu-related-record-btn"
                  onClick={() => onOpenRecord(r)}
                  style={{
                    flex: 1,
                    minWidth: 0,
                    textAlign: "left",
                    border: "1px solid var(--mid-gray)",
                    borderRadius: 8,
                    padding: "8px 10px",
                    fontSize: 13,
                    background: "var(--bg-0)",
                    cursor: "pointer",
                  }}
                >
                  <span
                    style={{
                      fontSize: 10.5,
                      fontWeight: 800,
                      textTransform: "uppercase",
                      color: "var(--dark-gray-light)",
                    }}
                  >
                    {r.kind}
                  </span>{" "}
                  <span
                    style={{
                      color: "var(--blue)",
                      fontWeight: 700,
                      textDecoration: "underline",
                      textUnderlineOffset: 2,
                    }}
                  >
                    {displayLabel(r.label)}
                  </span>
                </button>
                {page && (
                  <a
                    className="cu-related-record-open"
                    href={page}
                    target="_blank"
                    rel="noreferrer"
                    title={`Open ${displayLabel(r.label)} page`}
                    style={{
                      display: "inline-flex",
                      alignItems: "center",
                      gap: 4,
                      border: "1px solid var(--mid-gray)",
                      borderRadius: 8,
                      padding: "0 10px",
                      fontSize: 12,
                      fontWeight: 700,
                      color: "var(--blue)",
                      textDecoration: "none",
                      background: "var(--bg-0)",
                      whiteSpace: "nowrap",
                    }}
                  >
                    Open page
                    <ExternalLink size={12} strokeWidth={2} aria-hidden />
                  </a>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
